import { useState } from 'react';
import { getCopy } from '../utils/copy';

/**
 * 排序题(params.type === 'ordering'):点选依次放进序列槽,不用拖拽。
 *
 * 下方是打乱的候选词块,点一个按顺序进入上方带序号的槽;点槽里的项把它(及其后面的)
 * 退回,「撤销」退回最后一个,「重排」清空。全部放好后点「就这个顺序」提交,
 * answer 是 id 数组,由后端比对。答错只给温和提示,重试时从空槽开始。
 */
function OrderingPuzzle({ puzzle, onAnswer, feedback, disabled, ageBand }) {
  const { prompt, items } = puzzle.params;
  const [order, setOrder] = useState([]);
  const byId = Object.fromEntries(items.map((item) => [item.id, item]));
  const full = order.length === items.length;

  const place = (id) => {
    if (disabled || order.includes(id)) return;
    setOrder((prev) => [...prev, id]);
  };
  const removeAt = (index) => {
    if (disabled) return;
    setOrder((prev) => prev.slice(0, index));
  };

  return (
    <div className="flex w-full flex-col items-center gap-5 rounded-2xl border-2 border-black bg-white p-4 shadow-brutal sm:p-8">
      <p className="text-lg font-bold text-gray-800">{puzzle.title}</p>
      {prompt && (
        <p data-testid="puzzle-prompt" className="-mt-3 break-words text-center text-lg font-medium text-gray-700">
          {prompt}
        </p>
      )}
      {feedback && !feedback.correct && (
        <p role="alert" className="w-full text-center text-base font-medium text-orange-700">
          {getCopy(ageBand).retryHint}
        </p>
      )}

      <ol className="flex w-full flex-col gap-2" aria-label="排好的顺序">
        {items.map((_, index) => {
          const id = order[index];
          return (
            <li key={index} className="flex items-center gap-2">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border-2 border-black bg-qiji-gold text-base font-bold">
                {index + 1}
              </span>
              <button
                type="button"
                data-testid={`ordering-slot-${index}`}
                disabled={disabled || id === undefined}
                onClick={() => removeAt(index)}
                aria-label={id === undefined ? `第${index + 1}位,空` : `第${index + 1}位:${byId[id].label},点一下取回`}
                className={`min-h-[48px] min-w-0 flex-1 break-words rounded-xl border-2 px-3 py-2 text-left text-base font-semibold
                  ${id === undefined ? 'border-dashed border-gray-400 bg-amber-50/40 text-gray-400' : 'border-black bg-qiji-mint text-gray-900 shadow-brutal-sm'}`}
              >
                {id === undefined ? '' : byId[id].label}
              </button>
            </li>
          );
        })}
      </ol>

      <div className="flex w-full flex-wrap justify-center gap-2" aria-label="待排的词块">
        {items.map((item) => {
          const used = order.includes(item.id);
          return (
            <button
              key={item.id}
              type="button"
              data-testid={`ordering-item-${item.id}`}
              disabled={disabled || used}
              onClick={() => place(item.id)}
              className="min-h-[48px] max-w-full break-words rounded-xl border-2 border-black bg-amber-50 px-4 py-2 text-base
                         font-semibold text-gray-800 shadow-brutal-sm transition active:translate-y-0 active:shadow-none
                         disabled:opacity-30"
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div className="flex w-full gap-3">
        <button
          type="button"
          data-testid="ordering-undo"
          disabled={disabled || order.length === 0}
          onClick={() => setOrder((prev) => prev.slice(0, -1))}
          className="min-h-[48px] flex-1 rounded-lg border-2 border-black bg-white px-3 text-base font-bold text-gray-800 disabled:opacity-40"
        >
          撤销一步
        </button>
        <button
          type="button"
          data-testid="ordering-reset"
          disabled={disabled || order.length === 0}
          onClick={() => setOrder([])}
          className="min-h-[48px] flex-1 rounded-lg border-2 border-black bg-white px-3 text-base font-bold text-gray-800 disabled:opacity-40"
        >
          重排
        </button>
      </div>
      <button
        type="button"
        data-testid="ordering-submit"
        disabled={disabled || !full}
        onClick={() => onAnswer(order)}
        className="min-h-[48px] w-full rounded-lg border-2 border-black bg-qiji-mint px-6 py-3 text-base font-bold text-gray-900
                   shadow-brutal-sm transition active:translate-y-0 active:shadow-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        就这个顺序
      </button>
    </div>
  );
}

export default OrderingPuzzle;
